import useMatchQuestion from 'containers/NewQuestionnaire/hooks/useMatchQuestion';
import {Button, LoadingBox, useModals} from 'letrus-ui';
import {QuestionnaireStep} from 'APIs/letrusAPI/questionnaire/types';
import {
  fetchMatchOptionsRequest,
  fetchMatchResponseRequest,
  postMatchAnswersRequest,
} from 'APIs/letrusAPI/matchQuestion';
import {useMobile, useQueryParam} from 'utils/hooks';
import GeneralErrorModal from 'components/GeneralErrorModal';
import {MatchPairList} from 'APIs/letrusAPI/matchQuestion/types';
import styles from './MatchQuestion.module.scss';
import MatchOption from '../MatchOption';

export interface MatchQuestionProps {
  onReturn?(): void;
  isFirstQuestion?: boolean;
  learningPathStepId?: number;
  onAdvance?(answer?: string): void;
  question: QuestionnaireStep['question'];
}

function MatchQuestion({
  question,
  onReturn,
  onAdvance,
  isFirstQuestion,
  learningPathStepId,
}: MatchQuestionProps) {
  const queryParams = useQueryParam();
  const learningPathUserInstanceId = queryParams.get('trilha') ?? '';

  const {isMobile} = useMobile();
  const modals = useModals(['error']);

  const matchOptions = fetchMatchOptionsRequest(question.id);
  const postMatchAnswer = postMatchAnswersRequest();
  const matchResponses = fetchMatchResponseRequest({
    question_id: question.id,
    step_id: learningPathStepId,
    lpui_id: learningPathUserInstanceId,
  });

  const matchQuestion = useMatchQuestion(
    matchOptions.data,
    matchResponses.data?.responses,
  );

  function handleQuestionSubmit() {
    if (matchQuestion.hasBeenAnswered) {
      onAdvance?.(responseToString());
      return;
    }

    const userResponses = matchQuestion.options.right.map((pair) => ({
      pair_id: pair.pairId!,
      selected_option_id: pair.id,
    }));

    postMatchAnswer.trigger(
      {
        responses: userResponses,
        question_id: question?.id,
        step_id: learningPathStepId,
        source: isMobile ? 'app' : 'web',
        learning_path_user_instance_id: learningPathUserInstanceId,
      },
      {
        onError: () => modals.error?.showModal(),
        onSuccess: (data) => reFetchResponses(userResponses, data),
      },
    );
  }

  // only necessary for ai_tutor learning paths, it defines the first message
  function responseToString() {
    return (
      matchQuestion.options.left
        .map((leftOption) => {
          const rightOptionText = matchQuestion.options.right.find(
            (rightOption) => rightOption.pairId === leftOption.pairId,
          )?.text;
          return `${leftOption?.text} - ${rightOptionText}`;
        })
        .join('; ') ?? ''
    );
  }

  function reFetchResponses(userResponses: any, data?: MatchPairList) {
    const reviewedResponses = data?.responses;

    matchResponses.mutate({
      question_id: question?.id,
      responses: reviewedResponses ?? userResponses,
    });
  }

  return (
    <section className={styles.wrapper}>
      <div className={styles.header}>
        <h1>{question?.name}</h1>
        <div dangerouslySetInnerHTML={{__html: question?.text || ''}} />
      </div>

      <div className={styles.options}>
        {matchOptions.isLoading ? (
          <>
            <div className={styles.optionList}>
              <LoadingBox height="4rem" width="100%" borderRadius={8} />
              <LoadingBox height="4rem" width="100%" borderRadius={8} />
              <LoadingBox height="4rem" width="100%" borderRadius={8} />
              <LoadingBox height="4rem" width="100%" borderRadius={8} />
            </div>

            <div className={styles.optionList}>
              <LoadingBox height="4rem" width="100%" borderRadius={8} />
              <LoadingBox height="4rem" width="100%" borderRadius={8} />
              <LoadingBox height="4rem" width="100%" borderRadius={8} />
              <LoadingBox height="4rem" width="100%" borderRadius={8} />
            </div>
          </>
        ) : (
          <>
            <div className={styles.optionList}>
              {matchQuestion.options.left?.map((option, index) => (
                <MatchOption
                  key={option.id}
                  text={option.text}
                  pairNumber={index + 1}
                  disabled={matchQuestion?.hasBeenAnswered}
                  onClick={() => matchQuestion.update(option.id, 'left')}
                  selected={matchQuestion.activePair === option.id}
                  isCorrect={
                    matchQuestion?.responses?.find(
                      ({leftId}) => leftId === option.id,
                    )?.isCorrect
                  }
                />
              ))}
            </div>

            <div className={styles.optionList}>
              {matchQuestion.options.right?.map((option) => (
                <MatchOption
                  key={option.id}
                  text={option.text}
                  pairNumber={matchQuestion.pairNumber(
                    option.pairId ??
                      matchQuestion?.responses?.find(
                        ({rightId}) => rightId === option.id,
                      )?.pairId,
                  )}
                  disabled={matchQuestion?.hasBeenAnswered}
                  onClick={() => matchQuestion.update(option.id, 'right')}
                />
              ))}
            </div>
          </>
        )}
      </div>

      <div className={styles.controls}>
        <Button
          type="button"
          variant="secondary"
          onClick={onReturn}
          data-testid="return-button"
          disabled={isFirstQuestion}
        >
          Voltar
        </Button>

        <Button
          variant="primary"
          data-testid="submit-button"
          loading={matchResponses.isLoading}
          onClick={() => handleQuestionSubmit()}
          disabled={!matchQuestion.completed && !matchQuestion.hasBeenAnswered}
        >
          {matchQuestion.hasBeenAnswered
            ? 'Avançar'
            : question?.button_text || 'Avançar'}
        </Button>
      </div>

      <GeneralErrorModal
        id="error"
        title="Ops!"
        showIcon={false}
        subTitle="Não foi possível enviar sua resposta."
        description="Tente novamente mais tarde."
      />
    </section>
  );
}

export default MatchQuestion;
